/**
 * Bridges the YAML course registry with the DB course tables.
 *
 * The registry (`coursesRegistry.ts`) is synchronous and used across the app.
 * Rewriting it to be async would touch dozens of call sites, so instead this
 * hook loads the DB modules and returns a `CourseData` with `modules` replaced
 * by whatever the admin has configured.
 *
 * Behaviour:
 * - DB has modules for this course  → use them (admin is the source of truth)
 * - DB empty or unreachable         → fall back to the YAML modules
 *
 * That fallback keeps the site working before the migration SQL runs and if
 * Supabase is briefly unavailable.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { listModulesBySlug, type DbModule } from '@/lib/courseDb'
import type { CourseData, CourseModule } from '@/lib/coursesRegistry'

/**
 * Convierte una fila de la base al formato del registro.
 *
 * areaExamen viene de PV-Learn, donde el cero marcaba el modulo de
 * examen y contentModules lo descarta por eso. Aqui no hay examen
 * desde D200, asi que todos los modulos son de contenido: poner
 * cero los hacia desaparecer del temario.
 */
export function toCourseModule(m: DbModule, index: number): CourseModule {
  return {
    id: index + 1,
    slug: m.slug,
    titulo: m.title,
    duracionMin: m.durationMin,
    // Cualquier valor distinto de cero: son modulos de contenido
    areaExamen: index + 1,
    estado: 'producido',
    faseProduccion: 3,
    preguntas: 0,
  }
}

export interface CourseWithDbModules {
  course: CourseData | null
  /** True while the DB modules are still loading. */
  isLoading: boolean
  /** True when the modules come from the DB rather than the YAML fallback. */
  fromDb: boolean
}

/**
 * Returns the course with its module list sourced from the DB when available.
 * Pass the course straight from `getCourse(slug)`.
 */
export function useCourseModules(course: CourseData | null | undefined): CourseWithDbModules {
  const slug = course?.slug ?? ''

  const { data: dbModules, isLoading } = useQuery({
    queryKey: ['course-modules', slug],
    queryFn: () => listModulesBySlug(slug, false),
    enabled: Boolean(slug),
    staleTime: 60_000,
  })

  const merged = useMemo<CourseWithDbModules>(() => {
    if (!course) return { course: null, isLoading, fromDb: false }

    // No DB rows: keep the YAML modules untouched.
    if (!dbModules || dbModules.length === 0) {
      return { course, isLoading, fromDb: false }
    }

    return {
      course: { ...course, modules: dbModules.map(toCourseModule) },
      isLoading,
      fromDb: true,
    }
  }, [course, dbModules, isLoading])

  return merged
}

/**
 * Non-hook variant for code paths that already have the DB modules loaded.
 */
export function mergeDbModules(course: CourseData, dbModules: DbModule[]): CourseData {
  if (dbModules.length === 0) return course
  return { ...course, modules: dbModules.map(toCourseModule) }
}
